import type { JSX } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * The ROOM-VOICE illustration for the What-is section (P0-6) — the page SHOWING its single best idea
 * rather than describing it into a void. Two ducks stand together in a room, each emitting gentle, EQUAL
 * voice rings that meet in the middle: whoever shares a room shares one conversation, and everyone is
 * heard at the same full, clear volume. Built entirely from transform/opacity (60fps, GPU-cheap).
 *
 * Voice is flat + global-within-a-room (ADR-0057): no distance→volume fade, no left/right pan — so the
 * rings pulse steadily and EVENLY for both ducks, never swelling or shrinking with distance.
 *
 * REDUCED MOTION: we render ONE correct static frame — the two ducks with full, warm rings — and run no
 * animation. The mechanic still reads at a glance.
 *
 * ACCESSIBILITY: purely decorative (the adjacent copy carries the meaning) → the whole figure is
 * `aria-hidden`. No layout shift: the box has a fixed aspect ratio reserved up front.
 */
export function RoomVoice(): JSX.Element {
  const reduce = useReducedMotion();

  // Both ducks stand together in the room; their rings breathe gently and EQUALLY (no distance coupling)
  // — "we're in the same conversation", not "closer = louder". One slow, warm loop.
  const ring = reduce
    ? { scale: 1.05, opacity: 0.6 }
    : { scale: [0.9, 1.08, 0.9], opacity: [0.42, 0.68, 0.42] };
  const loop = { duration: 4.5, repeat: Infinity, ease: 'easeInOut' as const };

  return (
    <div
      aria-hidden="true"
      className="relative mx-auto aspect-[4/3] w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-[radial-gradient(120%_120%_at_50%_30%,rgba(255,210,63,0.10),transparent_60%)] ring-1 ring-white/5"
    >
      {/* Soft floor line so the ducks feel like they're standing in a room, not floating. */}
      <div className="absolute inset-x-8 bottom-12 h-px bg-white/10" />

      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative flex w-full max-w-[236px] items-center justify-between px-6">
          {/* ── Left duck + its rightward voice rings ── */}
          <div className="relative">
            <VoiceRings side="right" ring={ring} loop={loop} reduce={!!reduce} />
            <Duck facing="right" />
          </div>

          {/* ── Right duck + its leftward voice rings ── */}
          <div className="relative">
            <VoiceRings side="left" ring={ring} loop={loop} reduce={!!reduce} />
            <Duck facing="left" />
          </div>
        </div>
      </div>

      {/* A tiny caption pill — names the mechanic without shouting. */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full border border-amber-300/20 bg-[#0b1020]/70 px-3 py-1 text-[11px] font-semibold text-amber-200/90 backdrop-blur-sm">
        Same room, one conversation
      </div>
    </div>
  );
}

/** The concentric voice rings emanating from one duck (transform/opacity only) — steady + equal for both. */
function VoiceRings({
  side,
  ring,
  loop,
  reduce,
}: {
  side: 'left' | 'right';
  ring: { scale: number | number[]; opacity: number | number[] };
  loop: { duration: number; repeat: number; ease: 'easeInOut' };
  reduce: boolean;
}): JSX.Element {
  // Rings sit at the duck's "mouth" side and fan outward toward the partner.
  const origin = side === 'right' ? 'left-7 origin-left' : 'right-7 origin-right';
  return (
    <div className={`pointer-events-none absolute top-1/2 -translate-y-1/2 ${origin}`}>
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="absolute top-1/2 block -translate-y-1/2 rounded-full border-2 border-amber-300/70"
          style={{ width: 18 + i * 14, height: 18 + i * 14, marginTop: -(9 + i * 7) }}
          initial={false}
          animate={ring}
          transition={reduce ? undefined : { ...loop, delay: i * 0.18 }}
        />
      ))}
    </div>
  );
}

/** A small, friendly inline duck (round yellow body, amber beak, dot eye) — echoes the real critter art. */
function Duck({ facing }: { facing: 'left' | 'right' }): JSX.Element {
  return (
    <svg
      viewBox="0 0 40 40"
      className="relative h-14 w-14 drop-shadow-[0_4px_10px_rgba(0,0,0,0.35)]"
      style={facing === 'left' ? { transform: 'scaleX(-1)' } : undefined}
    >
      <defs>
        <linearGradient id={`rv-body-${facing}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffe07a" />
          <stop offset="1" stopColor="#f6b829" />
        </linearGradient>
      </defs>
      <circle cx="18" cy="20" r="11" fill={`url(#rv-body-${facing})`} />
      <circle cx="13.5" cy="15.5" r="3.4" fill="#fff3c4" fillOpacity={0.7} />
      <path d="M28 18.5c4-.6 6.2.5 6.2 2s-2.2 2.6-6.2 2Z" fill="#ff9d2e" />
      <circle cx="22" cy="17" r="1.9" fill="#1c2236" />
      <circle cx="22.7" cy="16.4" r="0.55" fill="#fff" />
    </svg>
  );
}
